import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import instance from "../../apiServices/instance";
import RiseFormationsLogo from "../../assets/logo/riseformations-logo.webp";

const EYEBROW_LABELS = [
  "BEST PRICE GUARANTEE",
  "KEEP HOME ADDRESS HIDDEN",
  "AMBITIOUS ENTREPRENEURS",
];

const FEATURE_INFO_TEXT = {
  "Company Incorporation":
    "Your Limited Company will usually be registered at Companies House within 24 to 48 hours.",
  "Incorporation Certificate":
    "The official document issued by Companies House confirming your company legally exists, including its name, registration number, and date of formation. It's often required as proof of incorporation when opening a business bank account or entering into contracts.",
  "Free Business Consultation":
    "A one-to-one session with one of our experts to discuss your new company, covering areas like tax, compliance, marketing and next steps. It's included at no cost to help you get started on the right footing.",
  "Free Business Bank Account":
    "UK Customers: We've partnered with the UK's leading banks to make the Business Bank Account opening experience seamless. Select your preferred bank when ordering, and they'll be in touch once your company has been incorporated. International Customers: Overseas customers will be shown UK Bank Accounts, which are happy to work with Foreign-based Directors.",
  "Shareholder Certificate":
    "All Shareholders will receive a shareholder certificate as part of the company incorporation.",
  "Memorandum of Articles":
    "These are the legal documents that establish your company and set out its internal rules. The Memorandum confirms the founding members' agreement to form the company, and is required by Companies House for every UK company.",
  "Articles of Association":
    "This sets out the rules for how your company will be run, covering things like directors' powers, shareholder rights, and decision-making procedures. It's a legal requirement for every UK company and is filed with Companies House at incorporation.",
  "1st Confirmation Statement Free":
    "A yearly filing that confirms your company's details held by Companies House are accurate and up to date, including directors, shareholders, and registered address. It's a legal requirement for every UK company, due within 14 days of your review period ending. Your 1st Confirmation Statement within this package is free.",
  "Central London Registered Address Service":
    "Use our prestigious Central London address as your company's registered office, keeping your home address private. Government and court correspondence is scanned and emailed to you daily, at no extra cost. Renews annually at £89 + VAT.",
  "Central London Service Address Service":
    "An official address for a director, shareholder or PSC on the public record. Government and court correspondence is scanned and emailed to you daily, at no extra cost. Renews annually at £19 + VAT.",
  "Central London Business Service":
    "Use our central London office for business correspondence and on your business website to create a London presence. Any mail scanned and uploaded to your client portal is included within the package at no additional cost. Renews annually at £89 + VAT.",
  "12 Months Free Company Secretarial Service":
    "Any changes or filings at Companies House - such as share allotments, change of directors, new shareholders, or PSCs - can all be completed within the Rise Formations portal, free of charge for the first 12 months.",
  "PAYE Registration":
    "If you want to pay yourself as a director or employ any individuals, you will need to register with HMRC for PAYE. We will complete the application process for you, which can typically take up to three weeks to process.",
  "VAT Registration":
    "Our team of tax experts will handle your VAT registration online. Once approved HMRC will send your VAT registration number by post, if you are using our registered address service, it will then appear within your Rise Formations client portal. Once approved our team can then help set up your Xero or QuickBooks accounting software or we can manage it on your behalf.",
  "Consultation with an Accountant":
    "Have a free 30-minute consultation with one of our accounting experts to fully understand what your compliance obligations are with both HMRC and Companies House. Feel free to use this time to ask any questions and further understand your accounting requirements.",
  "10% Discount on Business Insurance":
    "We've partnered with Kingsbridge Insurance to make sure that you can get the best deal on your professional indemnity or employer liability insurance. Access 10% off your business insurance when you sign up using the Plus Package.",
};

const normalizeFeatureText = (str) =>
  str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getFeatureInfoText = (feature) => {
  const normalized = normalizeFeatureText(feature);
  let bestMatch = null;
  let bestMatchLength = -1;

  Object.keys(FEATURE_INFO_TEXT).forEach((key) => {
    const normalizedKey = normalizeFeatureText(key);
    const isMatch =
      normalized.includes(normalizedKey) || normalizedKey.includes(normalized);
    if (isMatch && normalizedKey.length > bestMatchLength) {
      bestMatch = key;
      bestMatchLength = normalizedKey.length;
    }
  });

  return bestMatch ? FEATURE_INFO_TEXT[bestMatch] : null;
};

const Package = ({ handleClickSetSix }) => {
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState([]);
  const [companyHouseFee, setCompanyHouseFee] = useState("");
  const [addressService, setAddressService] = useState("");

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const buildSelectedPackage = (item) => ({
    ...item,
    companyHousePriceId: companyHouseFee[0]?.priceId,
    AddressServicePriceId: item.features.includes(
      "Central London Registered Address",
    )
      ? addressService[0]?.priceId
      : "",
  });

  const handleCardClick = (item) => {
    setSelectedPackage(buildSelectedPackage(item));
  };

  const handleSelectPackage = (event, item) => {
    event.stopPropagation();
    const selected = buildSelectedPackage(item);
    setSelectedPackage(selected);
    handleClickSetSix(selected);
  };
  const FetchPackageList = async () => {
    const { data } = await instance.get(`/api/stripe/products/fetch`);
    const companyHouseData = [];
    const packageData = [];
    const AddressServiceData = [];

    data.forEach(({ id, name, type, prices, coupon, features = [] }) => {
      const priceInPounds = prices?.[0]?.amount
        ? (prices[0].amount / 100).toFixed(2).replace(/\.00$/, "")
        : "0";

      const item = {
        id,
        packageName: name,
        price: priceInPounds,
        priceId: prices?.[0]?.id,
        // additionalFee: "+ £89 + VAT minimum Companies House fee",
        features,
        coupon,
      };

      if (type === "COMPANY_HOUSE_FEE") {
        companyHouseData.push(item);
      } else if (type === "ADDRESS_SERVICE") {
        AddressServiceData.push(item);
      } else {
        packageData.push(item);
      }
    });
    // Sort packageData by number of features (ascending)
    packageData.sort((a, b) => a.features.length - b.features.length);
    return { packageData, companyHouseData, AddressServiceData };
  };

  useEffect(() => {
    const fetchData = async () => {
      const { packageData, companyHouseData, AddressServiceData } =
        await FetchPackageList();
      setPackageData(packageData);
      const defaultPackage = packageData[1] || packageData[0];
      setSelectedPackage({
        ...defaultPackage,
        companyHousePriceId: companyHouseData?.[0]?.priceId || "",
        AddressServicePriceId: defaultPackage?.features?.includes(
          "Central London Registered Address",
        )
          ? AddressServiceData?.[0]?.priceId || ""
          : "",
      });
      setCompanyHouseFee(companyHouseData);
      setAddressService(AddressServiceData);
    };

    fetchData();
  }, []);

  const companyHousePrice = companyHouseFee[0]?.price;
  const baseFeatureSet = new Set(packageData[0]?.features || []);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems="center"
      sx={{ paddingTop: "0.5rem" }}
    >
      <Box
        sx={{
          width: "100%",
          padding: isSmallScreen ? "0.75rem 1rem" : "1rem 2.5rem",
        }}
      >
        <img
          src={RiseFormationsLogo}
          alt="Rise Formations"
          style={{ height: "1.75rem" }}
        />
      </Box>
      <Typography
        variant="h3"
        sx={{
          fontWeight: "700",
          fontSize: "1.875rem",
          color: "#0F0049",
          textAlign: "center",
          padding: isSmallScreen ? "0 1rem" : "0 2rem",
        }}
      >
        Choose your company formation package
      </Typography>
      {companyHousePrice && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.875rem",
            color: "#82808A",
            marginTop: "0.5rem",
            marginBottom: "2.5rem",
            textAlign: "center",
          }}
        >
          All packages include minimum Companies House fee{" "}
          <Box
            component="span"
            sx={{
              textDecoration: "line-through",
              fontWeight: 900,
              opacity: 0.6,
            }}
          >
            £100
          </Box>{" "}
          <Box component="span" sx={{ fontWeight: 600, color: "#0F0049" }}>
            £{companyHousePrice}
          </Box>{" "}
          on top
        </Typography>
      )}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        width="100%"
        gap={2}
        sx={{
          padding: isSmallScreen ? "0 1rem" : "0 2rem",
        }}
      >
        {packageData.length > 0 ? (
          packageData.map((item, index) => {
            const isRecommended = index === 1;
            const isSelected = selectedPackage.packageName === item.packageName;
            return (
              <Box
                key={index}
                display="flex"
                flexDirection="column"
                width={isSmallScreen ? "100%" : "30%"}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    color: "#82808A",
                    textAlign: "center",
                    marginBottom: "0.75rem",
                  }}
                >
                  {EYEBROW_LABELS[index] || ""}
                </Typography>
                <Box
                  onClick={() => handleCardClick(item)}
                  backgroundColor="white"
                  sx={{
                    position: "relative", // Important for placing the "Recommended" badge
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    border: isSelected
                      ? "2px solid #1f69f7"
                      : "1px solid #E0DFE5",
                    borderRadius: "1rem",
                    padding: "1rem",
                    boxShadow: isSelected
                      ? "0 4px 8px rgba(0,0,0,0.1)"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isRecommended && (
                    <Typography
                      sx={{
                        position: "absolute",
                        top: "-10px", // Adjust to place above the card
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#1f69f7",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        borderRadius: "0.5rem",
                        padding: "0.25rem 1rem",
                        textAlign: "center",
                      }}
                    >
                      Recommended
                    </Typography>
                  )}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.3rem",
                      mt: "0.5rem",
                    }}
                  >
                    {item.packageName}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, fontSize: "2rem", color: "#1f69f7" }}
                  >
                    £{item.price}
                  </Typography>
                  {companyHousePrice && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.9rem",
                        marginBottom: "0.7rem",
                        fontWeight: 600,
                      }}
                    >
                      +{" "}
                      <Box
                        component="span"
                        sx={{ textDecoration: "line-through" }}
                      >
                        £100
                      </Box>{" "}
                      £{companyHousePrice} Companies House Fee
                    </Typography>
                  )}
                  <Box
                    sx={{
                      borderBottom: "1px solid #E0DFE5",
                      marginBottom: "0.7rem",
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    {[...item.features]
                      .sort((a, b) => {
                        const aIsExtra = baseFeatureSet.has(a) ? 0 : 1;
                        const bIsExtra = baseFeatureSet.has(b) ? 0 : 1;
                        return aIsExtra - bIsExtra;
                      })
                      .map((feature, featureIndex, sortedFeatures) => {
                        const isPremium = !baseFeatureSet.has(feature);
                        const infoText = getFeatureInfoText(feature);
                        const previousFeature =
                          sortedFeatures[featureIndex - 1];
                        const isPreviousPremium =
                          previousFeature !== undefined &&
                          !baseFeatureSet.has(previousFeature);
                        const isFirstPremium = isPremium && !isPreviousPremium;
                        return (
                          <React.Fragment key={featureIndex}>
                            {isFirstPremium && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  margin: "0.6rem 0",
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: 1,
                                    borderBottom: "1px dashed #D6D4DC",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.05em",
                                    color: "#82808A",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  ALSO INCLUDES
                                </Typography>
                                <Box
                                  sx={{
                                    flex: 1,
                                    borderBottom: "1px dashed #D6D4DC",
                                  }}
                                />
                              </Box>
                            )}
                            <Box
                              display="flex"
                              alignItems="flex-start"
                              gap={0.5}
                              sx={{
                                padding: "0.25rem 0.35rem",
                                marginBottom: "0.15rem",
                                marginLeft: "-0.35rem",
                                marginRight: "-0.35rem",
                                borderRadius: "0.5rem",
                                transition: "background-color 0.15s ease",
                                "&:hover": { backgroundColor: "#F5F6FA" },
                              }}
                            >
                              <CheckIcon
                                sx={{
                                  fontSize: "1.1rem",
                                  color: "#1f69f7",
                                  mt: "0.1rem",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.9rem",
                                  color: isPremium ? "#1f69f7" : "inherit",
                                  fontWeight: isPremium ? 600 : 400,
                                  flex: 1,
                                }}
                              >
                                {feature}
                              </Typography>
                              {infoText && (
                                <Tooltip title={infoText} arrow placement="top">
                                  <IconButton
                                    aria-label={`More info about ${feature}`}
                                    size="small"
                                    sx={{
                                      padding: 0,
                                      mt: "0.05rem",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <InfoOutlinedIcon
                                      sx={{
                                        fontSize: "1rem",
                                        color: "#82808A",
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </React.Fragment>
                        );
                      })}
                  </Box>
                  <Button
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={(event) => handleSelectPackage(event, item)}
                    sx={{
                      marginTop: "1rem",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      borderRadius: "0.75rem",
                      padding: "0.5rem 1rem",
                      ...(isSelected
                        ? {
                            backgroundColor: "#1f69f7",
                            color: "#ffffff",
                            "&:hover": { backgroundColor: "#1859c6" },
                          }
                        : {
                            borderColor: "#E0DFE5",
                            color: "#0F0049",
                            "&:hover": { borderColor: "#1f69f7" },
                          }),
                    }}
                  >
                    Select
                  </Button>
                </Box>
              </Box>
            );
          })
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "20vh", // Adjust height as needed
            }}
          >
            <CircularProgress
              thickness={3.6}
              size={40}
              sx={{ color: "primary" }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Package;
